import { validateWorkflowGraphIntegrity, shouldHaveWorkflowGraphNode, getRootStepId } from '../graph/index.js';

// D2 (ADR-0001) — the SINGLE source of truth for where an `exit` step lands when the resolved
// parent is a trigger/input. Pure and typed: returns either `{ placement: <descriptor> }` or a
// typed `{ code, message, details }` rejection. Evaluated on the RAW input graph (never a
// normalized `{}`) so a desync (empty/null/malformed graph while action rows exist) is caught
// instead of masquerading as "genuinely empty". Both the executor preflight and
// createWorkflowStep consume this, so a case the preflight rejects can never be accepted by the
// producer (and vice-versa).
//
// Placement descriptors (explicit — consumers never re-infer topology):
//   { kind: 'create-root' }                            NEW is the sole root (null parent).
//   { kind: 'splice-after-parent', parentStepId }      parent.exit = NEW, NEW.exit = <old parent.exit>.
//   { kind: 'prepend-before-root', rootStepId }         NEW.exit = root; NEW becomes the root.
//
// Rejection codes:
//   'graph_integrity_error'   — desync / malformed / failed integrity validation.
//   'representation_mismatch' — the trigger/input parent is not a splice-able source.

const isPlainObject = (value) => !!value && typeof value === 'object' && !Array.isArray(value);

const rejection = (code, message, details) => ({ code, message, details });

export const resolveExitPlacement = ({ graph, steps, parentStepId }) => {
  const stepRows = Array.isArray(steps) ? steps : [];
  const actionRowCount = stepRows.filter((step) => shouldHaveWorkflowGraphNode(step)).length;

  const graphIsObject = isPlainObject(graph);
  const graphIsEmpty = graph === null || graph === undefined || (graphIsObject && Object.keys(graph).length === 0);

  // 1) Genuinely empty: zero nodes AND zero action rows → NEW is the sole root.
  if (graphIsEmpty && actionRowCount === 0) {
    return { placement: { kind: 'create-root' } };
  }

  // Desync guards on the RAW input (before any `|| {}` coercion could mask them).
  if (graphIsEmpty) {
    return rejection('graph_integrity_error', 'Workflow graph is empty but action steps still exist — the graph is out of sync with the step rows.', { reason: 'empty_graph_with_action_rows', actionRowCount });
  }
  if (!graphIsObject) {
    return rejection('graph_integrity_error', 'Workflow graph is malformed (expected an object).', { reason: 'malformed_graph' });
  }

  // 2) Input must pass integrity at ERROR level (orphan-row warnings are allowed).
  const integrity = validateWorkflowGraphIntegrity({ graph, steps: stepRows });
  if (!integrity.valid) {
    return rejection('graph_integrity_error', `Workflow graph failed integrity validation: ${integrity.errors.map((error) => error.code).join(', ')}.`, { reason: 'integrity_violation', errors: integrity.errors });
  }

  // Valid integrity ⇒ exactly one structural root.
  const rootStepId = getRootStepId(graph);
  const parentNode = graph[parentStepId];

  // 3) Parent materialized in the graph (legacy T -> A, source-only T). Valid source iff it is
  //    the sole structural root with no child/branch edges → splice through it.
  if (parentNode) {
    if (rootStepId !== parentStepId) {
      return rejection('representation_mismatch', `Cannot add a step after "${parentStepId}": it is not the workflow's starting step.`, { reason: 'parent_not_structural_root', rootStepId });
    }
    const hasChild = !!parentNode.child;
    const hasBranch = Array.isArray(parentNode.branches) && parentNode.branches.some((branch) => !!branch);
    if (hasChild || hasBranch) {
      return rejection('representation_mismatch', `Cannot add a step directly after "${parentStepId}": it has branch/child edges, so a linear insert is ambiguous.`, { reason: 'parent_has_child_or_branch' });
    }
    // The parent is the sole childless root, but its EXIT successor must itself be a valid
    // action head. Splicing NEW between the parent and a successor that is another trigger/input
    // would wedge an action between two triggers — e.g. legacy `T1 -> T2 -> A` with
    // parentStepId=T1 would produce `T1 -> NEW -> T2 -> A`. Reject that shape (ADR-0001's
    // nested-trigger fail-fast); a source-only `T` (no exit successor) still splices to `T -> NEW`.
    const successorStepId = parentNode.exit;
    if (successorStepId) {
      const successorStep = stepRows.find((step) => step.id === successorStepId);
      if (successorStep && !shouldHaveWorkflowGraphNode(successorStep)) {
        return rejection('representation_mismatch', `Cannot add a step after "${parentStepId}": its next step "${successorStepId}" is another trigger/input, so a linear insert would place an action between two triggers.`, { reason: 'successor_is_non_action_source', successorStepId });
      }
    }
    return { placement: { kind: 'splice-after-parent', parentStepId } };
  }

  // 4) Parent NOT in the graph (modern representation). The single structural root must be an
  //    action step → prepend NEW before it. A trigger/input root distinct from the parent is a
  //    representation mismatch.
  const rootStep = stepRows.find((step) => step.id === rootStepId);
  if (rootStep && !shouldHaveWorkflowGraphNode(rootStep)) {
    return rejection('representation_mismatch', `Cannot add a step at the start: the workflow root "${rootStepId}" is not an action step.`, { reason: 'root_is_non_action_source', rootStepId });
  }
  return { placement: { kind: 'prepend-before-root', rootStepId } };
};
